import { zodResolver } from "@hookform/resolvers/zod";
import SettingsIcon from "@mui/icons-material/Settings";
import LockIcon from "@mui/icons-material/Lock";
import ShieldIcon from "@mui/icons-material/Shield";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { z } from "zod";
import { authApi } from "@/api";
import {
  platformConfigApi,
  type PlatformConfig,
} from "@/api/platformConfig.api";
import { PageHeader } from "@/components/layout/PageHeader";
import { SettingsLayout } from "@/components/settings/SettingsLayout";
import { SettingsPanel } from "@/components/settings/SettingsPanel";
import type { SettingsNavItem } from "@/components/settings/SettingsNav";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { showApiError } from "@/utils/apiError";
import { notify } from "@/core/toast/notify";
import { MfaSettingsPanel } from "@/components/security/MfaSettingsPanel";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { useAppSelector } from "@/hooks/useAppSelector";
import { setMfaEnabled } from "@/store/authSlice";

const SECTIONS = ["platform", "password", "security"] as const;
type SettingsSection = (typeof SECTIONS)[number];

function isSettingsSection(value: string | null): value is SettingsSection {
  return SECTIONS.includes(value as SettingsSection);
}

const passwordSchema = z
  .object({
    currentPassword: z.string().min(1),
    newPassword: z.string().min(8),
    confirmPassword: z.string(),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: "Must match",
    path: ["confirmPassword"],
  });

type PasswordForm = z.infer<typeof passwordSchema>;

export function MasterSettingsPage() {
  const dispatch = useAppDispatch();
  const mfaEnabled = useAppSelector((s) => s.auth.mfaEnabled);
  const userEmail = useAppSelector((s) => s.auth.user?.email ?? '');
  const [searchParams, setSearchParams] = useSearchParams();
  const [config, setConfig] = useState<PlatformConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testMailTo, setTestMailTo] = useState('');
  const [testingMail, setTestingMail] = useState(false);

  const sectionParam = searchParams.get("section");
  const activeSection: SettingsSection = isSettingsSection(sectionParam)
    ? sectionParam
    : "platform";

  const navItems: SettingsNavItem[] = useMemo(
    () => [
      {
        id: "platform",
        label: "Platform config",
        description: "SSO, mail, CORS, and rate limits",
        icon: <SettingsIcon sx={{ fontSize: 18 }} />,
      },
      {
        id: "password",
        label: "Your password",
        description: "Update your sign-in password",
        icon: <LockIcon sx={{ fontSize: 18 }} />,
      },
      {
        id: "security",
        label: "Two-factor auth",
        description: "Protect your master account",
        icon: <ShieldIcon sx={{ fontSize: 18 }} />,
      },
    ],
    [],
  );

  const selectSection = (id: string) => {
    setSearchParams(id === "platform" ? {} : { section: id }, {
      replace: true,
    });
  };

  const [form, setForm] = useState({
    frontendUrl: "",
    corsOrigins: "",
    mfaIssuer: "",
    ssoEnabled: false,
    ssoCallbackBaseUrl: "",
    ssoGoogleEnabled: false,
    ssoGoogleClientId: "",
    ssoGoogleClientSecret: "",
    ssoMicrosoftEnabled: false,
    ssoMicrosoftClientId: "",
    ssoMicrosoftClientSecret: "",
    mailFrom: "",
    mailFromName: "",
    mailHost: "",
    mailPort: 587,
    mailUsername: "",
    mailPassword: "",
    rateLimitEnabled: true,
    rateLimitMax: 500,
    rateLimitAuthMax: 30,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await platformConfigApi.get();
      setConfig(data);
      setForm({
        frontendUrl: data.general.frontendUrl,
        corsOrigins: data.general.corsOrigins,
        mfaIssuer: data.security.mfaIssuer,
        ssoEnabled: data.sso.enabled,
        ssoCallbackBaseUrl: data.sso.callbackBaseUrl,
        ssoGoogleEnabled: data.sso.google.enabled,
        ssoGoogleClientId: data.sso.google.clientId ?? "",
        ssoGoogleClientSecret: "",
        ssoMicrosoftEnabled: data.sso.microsoft.enabled,
        ssoMicrosoftClientId: data.sso.microsoft.clientId ?? "",
        ssoMicrosoftClientSecret: "",
        mailFrom: data.mail.from,
        mailFromName: data.mail.fromName,
        mailHost: data.mail.host ?? "",
        mailPort: data.mail.port ?? 587,
        mailUsername: data.mail.username ?? "",
        mailPassword: "",
        rateLimitEnabled: data.rateLimit.enabled,
        rateLimitMax: data.rateLimit.maxRequestsPerMinute,
        rateLimitAuthMax: data.rateLimit.authMaxRequestsPerMinute,
      });
    } catch (err) {
      showApiError(err, "Failed to load platform settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (userEmail && !testMailTo) {
      setTestMailTo(userEmail);
    }
  }, [userEmail, testMailTo]);

  const sendTestMail = async () => {
    const to = testMailTo.trim();
    if (!to) {
      notify.error('Enter a recipient email for the test');
      return;
    }
    setTestingMail(true);
    try {
      await platformConfigApi.sendTestMail(to);
      notify.success('Test email sent — check the inbox (and spam folder)');
    } catch (err) {
      showApiError(err, 'Test email failed — save SMTP settings first, then retry');
    } finally {
      setTestingMail(false);
    }
  };

  const savePlatform = async () => {
    setSaving(true);
    try {
      const updated = await platformConfigApi.save({
        frontendUrl: form.frontendUrl,
        corsOrigins: form.corsOrigins,
        mfaIssuer: form.mfaIssuer,
        ssoEnabled: form.ssoEnabled,
        ssoCallbackBaseUrl: form.ssoCallbackBaseUrl,
        ssoGoogleEnabled: form.ssoGoogleEnabled,
        ssoGoogleClientId: form.ssoGoogleClientId,
        ssoGoogleClientSecret: form.ssoGoogleClientSecret || undefined,
        ssoMicrosoftEnabled: form.ssoMicrosoftEnabled,
        ssoMicrosoftClientId: form.ssoMicrosoftClientId,
        ssoMicrosoftClientSecret: form.ssoMicrosoftClientSecret || undefined,
        mailFrom: form.mailFrom,
        mailFromName: form.mailFromName,
        mailHost: form.mailHost,
        mailPort: form.mailPort,
        mailUsername: form.mailUsername,
        mailPassword: form.mailPassword || undefined,
        rateLimitEnabled: form.rateLimitEnabled,
        rateLimitMax: form.rateLimitMax,
        rateLimitAuthMax: form.rateLimitAuthMax,
      });
      setConfig(updated);
      setForm((f) => ({
        ...f,
        ssoGoogleClientSecret: "",
        ssoMicrosoftClientSecret: "",
        mailPassword: "",
      }));
      notify.success(
        "Platform settings saved — SSO, mail, and rate limits apply immediately",
      );
    } catch (err) {
      showApiError(err, "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<PasswordForm>({ resolver: zodResolver(passwordSchema) });

  const [pwdLoading, setPwdLoading] = useState(false);
  const onPassword = async (data: PasswordForm) => {
    setPwdLoading(true);
    try {
      await authApi.changePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      notify.success("Password changed");
      reset();
    } catch (err) {
      showApiError(err, "Failed to change password");
    } finally {
      setPwdLoading(false);
    }
  };

  const secretPlaceholder = (configured: boolean) =>
    configured ? "••••••••  (leave blank to keep)" : "Enter secret";

  return (
    <div className="page-content">
      <PageHeader
        title="Settings"
        description="Platform configuration, account security, and master admin preferences."
        breadcrumbs={[
          { label: "Master", to: "/master" },
          { label: "Settings" },
        ]}
      />

      <SettingsLayout
        nav={navItems}
        activeId={activeSection}
        onSelect={selectSection}
      >
        {activeSection === "security" && (
          <SettingsPanel
            id="settings-panel-security"
            title="Security"
            description="Add an extra layer of protection to your master account."
          >
            <MfaSettingsPanel
              initialEnabled={mfaEnabled}
              onStatusChange={(enabled) => dispatch(setMfaEnabled(enabled))}
            />
          </SettingsPanel>
        )}

        {activeSection === "password" && (
          <SettingsPanel
            id="settings-panel-password"
            title="Change password"
            description="Use a strong password with at least 8 characters."
          >
            <form
              onSubmit={handleSubmit(onPassword)}
              className="max-w-md space-y-4"
              noValidate
            >
              <Input
                label="Current password"
                type="password"
                autoComplete="current-password"
                {...register("currentPassword")}
                error={errors.currentPassword?.message}
              />
              <Input
                label="New password"
                type="password"
                autoComplete="new-password"
                {...register("newPassword")}
                error={errors.newPassword?.message}
              />
              <Input
                label="Confirm password"
                type="password"
                autoComplete="new-password"
                {...register("confirmPassword")}
                error={errors.confirmPassword?.message}
              />
              <Button type="submit" loading={pwdLoading}>
                Update password
              </Button>
            </form>
          </SettingsPanel>
        )}

        {activeSection === "platform" && (
          <div className="space-y-6">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Values saved here override{" "}
              <code className="rounded bg-slate-100 px-1 py-0.5 text-xs dark:bg-slate-800">
                application.yml
              </code>{" "}
              / environment for SSO, mail, CORS, and rate limits. Secrets are
              never shown after save.
            </p>

            {loading ? (
              <div
                className="flex justify-center py-12"
                aria-busy="true"
                aria-label="Loading platform settings"
              >
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
              </div>
            ) : (
              <>
                <SettingsPanel
                  id="settings-panel-general"
                  title="General"
                  description="Frontend URL, CORS, and MFA issuer."
                >
                  <div className="grid gap-4 md:grid-cols-2">
                    <Input
                      label="Frontend URL"
                      value={form.frontendUrl}
                      onChange={(e) =>
                        setForm({ ...form, frontendUrl: e.target.value })
                      }
                    />
                    <div className="md:col-span-2">
                      <Input
                        label="CORS allowed origins (comma-separated)"
                        value={form.corsOrigins}
                        onChange={(e) =>
                          setForm({ ...form, corsOrigins: e.target.value })
                        }
                      />
                    </div>
                    <Input
                      label="MFA issuer name"
                      value={form.mfaIssuer}
                      onChange={(e) =>
                        setForm({ ...form, mfaIssuer: e.target.value })
                      }
                    />
                  </div>
                </SettingsPanel>

                <SettingsPanel
                  id="settings-panel-sso"
                  title="SSO (Google & Microsoft)"
                  description="Configure OAuth providers for single sign-on."
                >
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="rounded border-slate-300 text-primary-600 focus:ring-primary-500/40"
                      checked={form.ssoEnabled}
                      onChange={(e) =>
                        setForm({ ...form, ssoEnabled: e.target.checked })
                      }
                    />
                    Enable SSO
                  </label>
                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <div className="md:col-span-2">
                      <Input
                        label="OAuth callback base URL"
                        placeholder="https://api.yourdomain.com/api"
                        value={form.ssoCallbackBaseUrl}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            ssoCallbackBaseUrl: e.target.value,
                          })
                        }
                      />
                    </div>
                    <fieldset className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
                      <legend className="px-1 text-sm font-medium">
                        Google
                      </legend>
                      <label className="mt-2 flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          className="rounded border-slate-300 text-primary-600 focus:ring-primary-500/40"
                          checked={form.ssoGoogleEnabled}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              ssoGoogleEnabled: e.target.checked,
                            })
                          }
                        />
                        Enabled
                      </label>
                      <Input
                        label="Client ID"
                        className="mt-3"
                        value={form.ssoGoogleClientId}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            ssoGoogleClientId: e.target.value,
                          })
                        }
                      />
                      <Input
                        label="Client secret"
                        className="mt-3"
                        type="password"
                        placeholder={secretPlaceholder(
                          config?.sso.google.clientSecretConfigured ?? false,
                        )}
                        value={form.ssoGoogleClientSecret}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            ssoGoogleClientSecret: e.target.value,
                          })
                        }
                      />
                    </fieldset>
                    <fieldset className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
                      <legend className="px-1 text-sm font-medium">
                        Microsoft
                      </legend>
                      <label className="mt-2 flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          className="rounded border-slate-300 text-primary-600 focus:ring-primary-500/40"
                          checked={form.ssoMicrosoftEnabled}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              ssoMicrosoftEnabled: e.target.checked,
                            })
                          }
                        />
                        Enabled
                      </label>
                      <Input
                        label="Client ID"
                        className="mt-3"
                        value={form.ssoMicrosoftClientId}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            ssoMicrosoftClientId: e.target.value,
                          })
                        }
                      />
                      <Input
                        label="Client secret"
                        className="mt-3"
                        type="password"
                        placeholder={secretPlaceholder(
                          config?.sso.microsoft.clientSecretConfigured ?? false,
                        )}
                        value={form.ssoMicrosoftClientSecret}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            ssoMicrosoftClientSecret: e.target.value,
                          })
                        }
                      />
                    </fieldset>
                  </div>
                </SettingsPanel>

                <SettingsPanel
                  id="settings-panel-mail"
                  title="Email (SMTP)"
                  description="Outgoing mail for notifications and password resets."
                >
                  {config?.mail.smtpReady ? (
                    <p className="mb-4 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                      Outbound email is configured (SMTP + from address)
                    </p>
                  ) : (
                    <p className="mb-4 text-xs text-amber-700 dark:text-amber-400">
                      Email is not fully configured — set From email, SMTP host, username, and password, then save.
                    </p>
                  )}
                  <p className="mb-4 text-xs text-slate-500 dark:text-slate-400">
                    Local: set <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">MAIL_*</code> in{' '}
                    <code className="rounded bg-slate-100 px-1 dark:bg-slate-800">backend/.env</code>. Production:
                    set host env on the server or save here (DB overrides env).
                  </p>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Input
                      label="From email"
                      type="email"
                      value={form.mailFrom}
                      onChange={(e) =>
                        setForm({ ...form, mailFrom: e.target.value })
                      }
                    />
                    <Input
                      label="From name"
                      value={form.mailFromName}
                      onChange={(e) =>
                        setForm({ ...form, mailFromName: e.target.value })
                      }
                    />
                    <Input
                      label="SMTP host"
                      value={form.mailHost}
                      onChange={(e) =>
                        setForm({ ...form, mailHost: e.target.value })
                      }
                    />
                    <Input
                      label="Port"
                      type="number"
                      value={form.mailPort}
                      onChange={(e) =>
                        setForm({ ...form, mailPort: Number(e.target.value) })
                      }
                    />
                    <Input
                      label="SMTP username"
                      value={form.mailUsername}
                      onChange={(e) =>
                        setForm({ ...form, mailUsername: e.target.value })
                      }
                    />
                    <Input
                      label="SMTP password"
                      type="password"
                      placeholder={secretPlaceholder(
                        config?.mail.passwordConfigured ?? false,
                      )}
                      value={form.mailPassword}
                      onChange={(e) =>
                        setForm({ ...form, mailPassword: e.target.value })
                      }
                    />
                  </div>
                  <div className="mt-4 flex flex-wrap items-end gap-3 border-t border-slate-200 pt-4 dark:border-slate-700">
                    <div className="min-w-[min(100%,20rem)] flex-1">
                      <Input
                        label="Send test email to"
                        type="email"
                        value={testMailTo}
                        onChange={(e) => setTestMailTo(e.target.value)}
                        placeholder="you@example.com"
                      />
                    </div>
                    <Button
                      variant="secondary"
                      loading={testingMail}
                      disabled={!config?.mail.smtpReady}
                      onClick={() => void sendTestMail()}
                    >
                      Send test email
                    </Button>
                  </div>
                </SettingsPanel>

                <SettingsPanel
                  id="settings-panel-ratelimit"
                  title="Rate limiting"
                  description="Protect the API from excessive request volume."
                >
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="rounded border-slate-300 text-primary-600 focus:ring-primary-500/40"
                      checked={form.rateLimitEnabled}
                      onChange={(e) =>
                        setForm({ ...form, rateLimitEnabled: e.target.checked })
                      }
                    />
                    Enabled
                  </label>
                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <Input
                      label="Max requests / minute"
                      type="number"
                      value={form.rateLimitMax}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          rateLimitMax: Number(e.target.value),
                        })
                      }
                    />
                    <Input
                      label="Auth endpoints max / minute"
                      type="number"
                      value={form.rateLimitAuthMax}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          rateLimitAuthMax: Number(e.target.value),
                        })
                      }
                    />
                  </div>
                </SettingsPanel>

                <div className="flex flex-wrap gap-3">
                  <Button onClick={() => void savePlatform()} loading={saving}>
                    Save platform settings
                  </Button>
                  <Button variant="secondary" onClick={() => void load()}>
                    Reload
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </SettingsLayout>
    </div>
  );
}
